// =============================================================================
// Step 1 — Customer & Service Selection (catalog + custom event modes)
// =============================================================================

import {
  User,
  Briefcase,
  Clock,
  Check,
  Search,
  Plus,
  Palette,
  Trash2,
  Settings2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { L } from '../../../config/labels';
import type { ServiceDefinition, Customer } from '../../../types';
import { useBooking } from './BookingContext';
import { COLOR_PRESETS } from './types';

// ===========================================================================
// Main Step Component
// ===========================================================================

export function CustomerServiceStep() {
  const { t } = useTranslation('scheduler');
  const { state, dispatch } = useBooking();

  return (
    <div className="space-y-6">
      {/* Mode Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => dispatch({ type: 'SET_BOOKING_MODE', payload: 'catalog' })}
          className={cn(
            'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            state.bookingMode === 'catalog'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          <Briefcase className="w-4 h-4 inline mr-2" />
          {t('booking.serviceCatalog')}
        </button>
        <button
          onClick={() => dispatch({ type: 'SET_BOOKING_MODE', payload: 'custom' })}
          className={cn(
            'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            state.bookingMode === 'custom'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          <Settings2 className="w-4 h-4 inline mr-2" />
          {t('booking.customEvent')}
        </button>
      </div>

      {state.bookingMode === 'catalog' ? <CatalogForm /> : <CustomEventForm />}
    </div>
  );
}

// ===========================================================================
// Catalog Form — service list + customer selection
// ===========================================================================

function CatalogForm() {
  const { t } = useTranslation('scheduler');
  const {
    state,
    dispatch,
    services,
    servicesLoading,
    customers,
    customersLoading,
    selectedService,
    createCustomer,
    handleCreateCustomer,
  } = useBooking();

  return (
    <>
      {/* Service Selection */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <Briefcase className="w-4 h-4" />
          {L.service}
        </label>

        <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
          {servicesLoading ? (
            <div className="p-4 text-center text-gray-500">{t('common:status.loading')}</div>
          ) : services.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('booking.noServicesAvailable', { entity: L.service.toLowerCase() })}
            </div>
          ) : (
            services.map((service: ServiceDefinition) => (
              <button
                key={service.id}
                onClick={() => dispatch({ type: 'SET_SERVICE_ID', payload: service.id })}
                className={cn(
                  'w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0',
                  state.selectedServiceId === service.id && 'bg-emerald-50 hover:bg-emerald-50',
                )}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{service.name}</span>
                    {!service.requiresClient && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {t('booking.internal')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-3">
                    <span>{service.durationMinutes} min</span>
                    {service.basePrice && <span>€{service.basePrice}</span>}
                  </div>
                </div>
                {state.selectedServiceId === service.id && (
                  <Check className="w-5 h-5 text-emerald-600" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Customer Selection — only if service requires a client */}
      {selectedService?.requiresClient !== false && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4" />
            {L.customer}
          </label>

          {!state.showNewCustomerForm ? (
            <>
              {/* Search */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('booking.searchCustomer', { entity: L.customer.toLowerCase() })}
                  value={state.customerSearch}
                  onChange={e =>
                    dispatch({ type: 'SET_CUSTOMER_SEARCH', payload: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-2 input"
                />
              </div>

              {/* List */}
              <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {customersLoading ? (
                  <div className="p-4 text-center text-gray-500">{t('common:status.loading')}</div>
                ) : customers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {t('booking.noCustomerFound', { entity: L.customer.toLowerCase() })}
                  </div>
                ) : (
                  customers.map((customer: Customer) => (
                    <button
                      key={customer.id}
                      onClick={() =>
                        dispatch({ type: 'SET_CUSTOMER_ID', payload: customer.id })
                      }
                      className={cn(
                        'w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0',
                        state.selectedCustomerId === customer.id &&
                          'bg-emerald-50 hover:bg-emerald-50',
                      )}
                    >
                      <div>
                        <div className="font-medium text-gray-900">
                          {customer.firstName} {customer.lastName}
                        </div>
                        {customer.email && (
                          <div className="text-sm text-gray-500">{customer.email}</div>
                        )}
                      </div>
                      {state.selectedCustomerId === customer.id && (
                        <Check className="w-5 h-5 text-emerald-600" />
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* New customer button */}
              <button
                onClick={() =>
                  dispatch({ type: 'SET_SHOW_NEW_CUSTOMER_FORM', payload: true })
                }
                className="mt-2 flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
              >
                <Plus className="w-4 h-4" />
                {t('booking.newCustomer', { entity: L.customer.toLowerCase() })}
              </button>
            </>
          ) : (
            /* New Customer Form */
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder={t('booking.firstName')}
                  value={state.newCustomer.firstName}
                  onChange={e =>
                    dispatch({
                      type: 'UPDATE_NEW_CUSTOMER',
                      payload: { firstName: e.target.value },
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  placeholder={t('booking.lastName')}
                  value={state.newCustomer.lastName}
                  onChange={e =>
                    dispatch({
                      type: 'UPDATE_NEW_CUSTOMER',
                      payload: { lastName: e.target.value },
                    })
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <input
                type="email"
                placeholder={t('common:fields.email')}
                value={state.newCustomer.email}
                onChange={e =>
                  dispatch({
                    type: 'UPDATE_NEW_CUSTOMER',
                    payload: { email: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="tel"
                placeholder={t('common:fields.phone')}
                value={state.newCustomer.phone}
                onChange={e =>
                  dispatch({
                    type: 'UPDATE_NEW_CUSTOMER',
                    payload: { phone: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    dispatch({ type: 'SET_SHOW_NEW_CUSTOMER_FORM', payload: false })
                  }
                  className="flex-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  onClick={handleCreateCustomer}
                  disabled={
                    !state.newCustomer.firstName ||
                    !state.newCustomer.lastName ||
                    createCustomer.isPending
                  }
                  className="btn-primary flex-1 px-3 py-2"
                >
                  {createCustomer.isPending ? t('common:status.saving') : t('common:buttons.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Internal service info */}
      {selectedService?.requiresClient === false && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            {t('booking.internalServiceHint')}
          </p>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Custom Event Form — title, duration, color, client toggle, resources
// ===========================================================================

function CustomEventForm() {
  const { t } = useTranslation('scheduler');
  const { state, dispatch, customers, customersLoading, resourceTypes } = useBooking();
  const { customEvent, showResourcePicker } = state;

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <Settings2 className="w-4 h-4" />
          {t('booking.eventTitle')}
        </label>
        <input
          type="text"
          placeholder={t('booking.eventTitlePlaceholder')}
          value={customEvent.title}
          onChange={e =>
            dispatch({ type: 'UPDATE_CUSTOM_EVENT', payload: { title: e.target.value } })
          }
          className="w-full px-4 py-2 input"
        />
      </div>

      {/* Duration */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <Clock className="w-4 h-4" />
          {t('booking.duration', { value: customEvent.duration })}
        </label>
        <input
          type="range"
          min="5"
          max="480"
          step="5"
          value={customEvent.duration}
          onChange={e =>
            dispatch({
              type: 'UPDATE_CUSTOM_EVENT',
              payload: { duration: parseInt(e.target.value) },
            })
          }
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{t('booking.minDuration')}</span>
          <span>{t('booking.maxDuration')}</span>
        </div>
      </div>

      {/* Color */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <Palette className="w-4 h-4" />
          {t('common:fields.color')}
        </label>
        <div className="flex flex-wrap gap-2">
          {COLOR_PRESETS.map(preset => (
            <button
              key={preset.value}
              type="button"
              onClick={() =>
                dispatch({ type: 'UPDATE_CUSTOM_EVENT', payload: { color: preset.value } })
              }
              className={cn(
                'w-8 h-8 rounded-full border-2 transition-all',
                customEvent.color === preset.value
                  ? 'border-gray-800 scale-110'
                  : 'border-transparent hover:scale-105',
              )}
              style={{ backgroundColor: preset.value }}
              title={t(`common:${preset.labelKey}`)}
            />
          ))}
        </div>
      </div>

      {/* Requires Client Toggle */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-700">{t('booking.requiresCustomer', { entity: L.customer })}</div>
          <div className="text-xs text-gray-500">
            {t('booking.requiresCustomerHint', { entity: L.customer.toLowerCase() })}
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            dispatch({
              type: 'UPDATE_CUSTOM_EVENT',
              payload: { requiresClient: !customEvent.requiresClient },
            })
          }
          className={cn(
            'relative w-12 h-6 rounded-full transition-colors',
            customEvent.requiresClient ? 'bg-emerald-600' : 'bg-gray-300',
          )}
        >
          <div
            className={cn(
              'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform',
              customEvent.requiresClient ? 'left-7' : 'left-1',
            )}
          />
        </button>
      </div>

      {/* Max clienti (eventi di gruppo, solo con cliente) */}
      {customEvent.requiresClient && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-700">{t('booking.maxClients')}</div>
            <div className="text-xs text-gray-500">{t('booking.maxClientsCustomHint')}</div>
          </div>
          <input
            type="number"
            min={1}
            max={500}
            value={customEvent.maxClients}
            onChange={e =>
              dispatch({
                type: 'UPDATE_CUSTOM_EVENT',
                payload: { maxClients: Math.max(1, parseInt(e.target.value) || 1) },
              })
            }
            className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      )}

      {/* Customer Selection (if requires client) */}
      {customEvent.requiresClient && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4" />
            {L.customer}
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('booking.searchCustomer', { entity: L.customer.toLowerCase() })}
              value={state.customerSearch}
              onChange={e =>
                dispatch({ type: 'SET_CUSTOMER_SEARCH', payload: e.target.value })
              }
              className="w-full pl-10 pr-4 py-2 input"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
            {customersLoading ? (
              <div className="p-3 text-center text-gray-500 text-sm">{t('common:status.loading')}</div>
            ) : customers.length === 0 ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                {t('booking.noCustomerFound', { entity: L.customer.toLowerCase() })}
              </div>
            ) : (
              customers.map((customer: Customer) => (
                <button
                  key={customer.id}
                  onClick={() =>
                    dispatch({ type: 'SET_CUSTOMER_ID', payload: customer.id })
                  }
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0',
                    state.selectedCustomerId === customer.id &&
                      'bg-emerald-50 hover:bg-emerald-50',
                  )}
                >
                  <span className="font-medium text-gray-900">
                    {customer.firstName} {customer.lastName}
                  </span>
                  {state.selectedCustomerId === customer.id && (
                    <Check className="w-4 h-4 text-emerald-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Resource Requirements */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <Settings2 className="w-4 h-4" />
          {t('booking.resourceRequirements')}
        </label>

        {customEvent.resourceRequirements.length > 0 && (
          <div className="space-y-2 mb-3">
            {customEvent.resourceRequirements.map((req, index) => {
              const rt = resourceTypes.find(r => r.id === req.resourceTypeId);
              return (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="flex-1 text-sm">
                    {rt?.name || t('booking.unknownType')} x {req.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({ type: 'REMOVE_RESOURCE_REQUIREMENT', payload: index })
                    }
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!showResourcePicker ? (
          <button
            type="button"
            onClick={() => dispatch({ type: 'SET_SHOW_RESOURCE_PICKER', payload: true })}
            className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
          >
            <Plus className="w-4 h-4" />
            {t('booking.addResourceRequirement')}
          </button>
        ) : (
          <div className="border border-gray-200 rounded-lg p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select
                id="resource-type-select"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                defaultValue=""
              >
                <option value="" disabled>
                  {t('booking.resourceTypePlaceholder')}
                </option>
                {resourceTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </select>
              <input
                id="resource-qty-input"
                type="number"
                min="1"
                max="10"
                defaultValue="1"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder={t('booking.quantity')}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  dispatch({ type: 'SET_SHOW_RESOURCE_PICKER', payload: false })
                }
                className="flex-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const selectEl = document.getElementById(
                    'resource-type-select',
                  ) as HTMLSelectElement;
                  const qtyEl = document.getElementById(
                    'resource-qty-input',
                  ) as HTMLInputElement;
                  if (selectEl?.value && qtyEl?.value) {
                    dispatch({
                      type: 'ADD_RESOURCE_REQUIREMENT',
                      payload: {
                        resourceTypeId: selectEl.value,
                        quantity: parseInt(qtyEl.value) || 1,
                      },
                    });
                  }
                }}
                className="btn-primary flex-1 px-3 py-2 text-sm"
              >
                {t('common:buttons.add')}
              </button>
            </div>
          </div>
        )}

        <p className={cn(
          "text-xs mt-2",
          customEvent.resourceRequirements.length === 0
            ? "text-amber-600 font-medium"
            : "text-gray-500"
        )}>
          {t('booking.emptyResourcesHint')}
        </p>
      </div>
    </div>
  );
}
